import _ from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
  DictionaryObject,
  EmptyComponent,
  FormContext,
  FormPanel,
  FormSelect,
  FormTableField,
  FormTextField,
  SnackbarContext,
  SnackbarVariant,
  TableFieldCells,
  useFormSelector,
  useStaticListSource,
} from '@eas/common-web';
import { StaticDataContext } from '@components/evidence/static-data-provider/static-data-provider';
import { CityField } from '@components/form/address/city-field';
import { ZipCodeField } from '@components/form/address/zip-code-field';
import { FormTableCount } from '@components/form/table-field/form-table-count';
import { Contact, IdentifiedPersonDto, Subject } from '@models';
import { EvidenceApiUrl, SubjectType } from '@enums';

function AddressField({ prefix, label }: { prefix?: string; label?: string }) {
  const { setFieldValue } = useContext(FormContext);

  return (
    <FormPanel label={label}>
      <FormTextField name={`${prefix}.street`} label="Ulice" />
      <FormTextField
        name={`${prefix}.descriptiveNumber`}
        label="Číslo popisné"
      />
      <FormTextField
        name={`${prefix}.orientationNumber`}
        label="Číslo orientační"
      />
      <ZipCodeField prefix={prefix} />
      <CityField prefix={prefix} />
      <FormTextField
        name={`${prefix}.locationCode`}
        label="Kód adresního místa"
        notifyChange={() => {
          setFieldValue('selectedSubject.identification', null);
        }}
      />
    </FormPanel>
  );
}

export function IdentifyDialog({
  contact,
  subject,
}: {
  contact?: Contact;
  subject?: Subject;
}) {
  const { setFieldValue } = useContext(FormContext);
  const { subjectTypes, subjectSexes } = useContext(StaticDataContext);
  const { showSnackbar } = useContext(SnackbarContext);

  const [loading, setLoading] = useState(false);

  const subjectTypeSource = useStaticListSource(subjectTypes);
  const useSubjectTypes = () => useStaticListSource(subjectTypes);
  const subjectSexSource = useStaticListSource(subjectSexes);

  const { selectedSubject } = useFormSelector(
    (data: { selectedSubject: IdentifiedPersonDto }) => ({
      selectedSubject: data.selectedSubject,
    })
  );

  useEffect(() => {
    const loadSubjects = async () => {
      const body = JSON.stringify({
        ..._.pick(contact, ['dataBox', 'address']),
        subject,
      });

      setLoading(true);
      const response = await fetch(
        `${EvidenceApiUrl.SUBJECT_CONTACTS}/identify`,
        {
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' }),
          body,
        }
      );

      if (response.ok) {
        const subjects = await response.json();
        setFieldValue('subjects', subjects);
      } else {
        const error = await response.json();

        showSnackbar(
          error.message ?? 'Neznámá chyba při načtení kontaktů',
          SnackbarVariant.ERROR
        );
      }

      setLoading(false);
    };

    loadSubjects();
  }, []);

  const columns = [
    {
      datakey: 'subjectType',
      name: 'Typ subjektu',
      width: 200,
      CellComponent: TableFieldCells.useSelectCellFactory<
        IdentifiedPersonDto,
        DictionaryObject
      >(useSubjectTypes),
    },
    {
      datakey: 'firstName',
      name: 'Jméno',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'lastName',
      name: 'Příjmení',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'maidenName',
      name: 'Rodné příjmení',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'companyName',
      name: 'Název firmy',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'ico',
      name: 'IČO',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'address.label',
      name: 'Adresa pobytu / sídla',
      width: 250,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'deliveryAddress.label',
      name: 'Doručovací adresa',
      width: 250,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'email',
      name: 'Email',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
    {
      datakey: 'phoneNumber',
      name: 'Telefonní číslo',
      width: 150,
      CellComponent: TableFieldCells.TextCell,
    },
  ];

  return (
    <>
      {loading && (
        <div
          style={{ display: 'flex', justifyContent: 'center', width: '100%' }}
        >
          <CircularProgress />
        </div>
      )}
      {!loading && (
        <>
          <FormPanel
            label={
              <FormattedMessage
                id="ES__CONTACTS__IDENTIFY_CONTACT_DIALOG__PANEL_TITLE__RESULTS"
                defaultMessage="Nalezené výsledky"
              />
            }
            expandable={false}
            summary={<FormTableCount formKey="subjects" />}
          >
            <FormTableField
              name="subjects"
              labelOptions={{
                hide: true,
              }}
              layoutOptions={{ noSpacing: true }}
              ToolbarComponent={EmptyComponent}
              maxRows={5}
              onSelect={async (row) => {
                setFieldValue('selectedSubject', row);
              }}
              columns={columns}
            />
          </FormPanel>
          {selectedSubject && (
            <>
              <FormPanel
                label={
                  <FormattedMessage
                    id="ES__CONTACTS__IDENTIFY_CONTACT_DIALOG__PANEL_TITLE__SELECTED_SUBJECT"
                    defaultMessage="Subjekt"
                  />
                }
                expandable={false}
              >
                <FormSelect
                  source={subjectTypeSource}
                  name="selectedSubject.subjectType"
                  label="Typ subjektu"
                  disabled
                  valueIsId
                />
                {selectedSubject?.subjectType !== SubjectType.LEGAL_ENTITY && (
                  <>
                    <FormTextField
                      name="selectedSubject.firstName"
                      label="Jméno"
                      notifyChange={() => {
                        setFieldValue('selectedSubject.identification', null);
                      }}
                    />
                    <FormTextField
                      name="selectedSubject.lastName"
                      label="Příjmení"
                      notifyChange={() => {
                        setFieldValue('selectedSubject.identification', null);
                      }}
                    />
                    <FormTextField
                      name="selectedSubject.maidenName"
                      label="Rodné příjmení"
                    />
                    <FormSelect
                      name="selectedSubject.sex"
                      label="Pohlaví"
                      source={subjectSexSource}
                      valueIsId
                    />
                  </>
                )}
                {selectedSubject?.subjectType !==
                  SubjectType.NATURAL_PERSON && (
                  <>
                    <FormTextField
                      name="selectedSubject.companyName"
                      label="Název firmy"
                      notifyChange={() => {
                        setFieldValue('selectedSubject.identification', null);
                      }}
                    />
                    <FormTextField
                      name="selectedSubject.ico"
                      label="IČO"
                      notifyChange={() => {
                        setFieldValue('selectedSubject.identification', null);
                      }}
                    />
                  </>
                )}

                <FormTextField name="selectedSubject.email" label="Email" />
                <FormTextField
                  name="selectedSubject.phoneNumber"
                  label="Telefonní číslo"
                />
              </FormPanel>
              <AddressField
                prefix="selectedSubject.address"
                label="Adresa trvalého pobytu"
              />
              {selectedSubject.deliveryAddress && (
                <AddressField
                  prefix="selectedSubject.deliveryAddress"
                  label="Doručovací adresa"
                />
              )}
            </>
          )}
        </>
      )}
    </>
  );
}
